import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MODES = [
  { v: "simple", l: "Simple (tu provincia / resto del país)" },
  { v: "meetup_only", l: "Solo encuentro presencial" },
  { v: "detailed", l: "Detallado (por provincia)" },
];
const errMsg = (e) => e?.response?.data?.detail || "Error";
const fmt = (n) => (n === null || n === undefined || n === "" ? "—" : `$${Number(n).toFixed(2)}`);

function Summary({ s }) {
  const c = s.shipping_config || {};
  return (
    <div className="text-xs text-neutral-400 grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid={`ship-summary-${s.id}`}>
      <div>Modo: <b className="text-neutral-200">{c.mode || "sin configurar"}</b></div>
      <div>Provincia del vendedor: <b className="text-neutral-200">{s.province || "—"}</b></div>
      <div>Tarifa su provincia: <b className="text-neutral-200">{fmt(c.same_province_rate)}</b></div>
      <div>Tarifa resto del país: <b className="text-neutral-200">{fmt(c.other_province_rate)}</b></div>
      <div>Solo envíos en su provincia: <b className={c.local_only ? "text-amber-300" : "text-neutral-200"}>{c.local_only ? `Sí (${s.province || "?"})` : "No"}</b></div>
      <div>Encuentro presencial: <b className="text-neutral-200">{c.meetup_enabled ? "Sí" : "No"}</b>{c.meetup_notes ? ` · ${c.meetup_notes}` : ""}</div>
      <div>Publicaciones activas: <b className="text-neutral-200">{s.active_pubs ?? "—"}</b></div>
    </div>
  );
}

function Editor({ s, provinces, onSaved }) {
  const c = s.shipping_config || {};
  const [f, setF] = useState({
    mode: c.mode || "simple", same_province_rate: c.same_province_rate ?? "", other_province_rate: c.other_province_rate ?? "",
    local_only: !!c.local_only, province: s.province || "", meetup_enabled: !!c.meetup_enabled, meetup_notes: c.meetup_notes || "",
    per_province_rates: c.per_province_rates || {},
  });
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const num = (v) => (v === "" || v === null ? null : Number(v));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/admin/users/${s.id}/shipping-config`, {
        ...f, same_province_rate: num(f.same_province_rate), other_province_rate: num(f.other_province_rate), province: f.province || null,
      });
      toast.success(`Envío guardado · ${r.data.publications_synced} publicaciones sincronizadas`);
      onSaved(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const lbl = "text-[11px] text-neutral-400 block mb-1";
  return (
    <div className="mt-3 p-3 rounded border border-neutral-700 bg-neutral-900/60 space-y-3" data-testid={`ship-editor-${s.id}`}>
      <div className="grid sm:grid-cols-3 gap-3">
        <label><span className={lbl}>Modo</span>
          <select value={f.mode} onChange={(e) => set({ mode: e.target.value })} className="w-full h-9 bg-neutral-950 border border-neutral-700 rounded px-2 text-sm" data-testid="ship-mode">
            {MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select></label>
        <label><span className={lbl}>Tarifa en su provincia ($)</span>
          <Input type="number" min="0" step="0.01" value={f.same_province_rate} onChange={(e) => set({ same_province_rate: e.target.value })} disabled={f.mode !== "simple"} data-testid="ship-same-rate" /></label>
        <label><span className={lbl}>Tarifa resto del país ($)</span>
          <Input type="number" min="0" step="0.01" value={f.other_province_rate} onChange={(e) => set({ other_province_rate: e.target.value })} disabled={f.mode !== "simple" || f.local_only} placeholder={f.local_only ? "No aplica (solo su provincia)" : ""} className="disabled:opacity-60 disabled:bg-neutral-800" data-testid="ship-other-rate" /></label>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 items-end">
        <label><span className={lbl}>Provincia del vendedor</span>
          <select value={f.province} onChange={(e) => set({ province: e.target.value })} className="w-full h-9 bg-neutral-950 border border-neutral-700 rounded px-2 text-sm" data-testid="ship-province">
            <option value="">—</option>
            {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
          </select></label>
        <label className="flex items-center gap-2 text-sm h-9"><input type="checkbox" checked={f.local_only} onChange={(e) => set({ local_only: e.target.checked })} data-testid="ship-local-only" />Solo envíos dentro de su provincia</label>
        <label className="flex items-center gap-2 text-sm h-9"><input type="checkbox" checked={f.meetup_enabled} onChange={(e) => set({ meetup_enabled: e.target.checked })} data-testid="ship-meetup" />Encuentro presencial</label>
      </div>
      {f.meetup_enabled && (
        <label className="block"><span className={lbl}>Puntos de encuentro</span>
          <Input value={f.meetup_notes} onChange={(e) => set({ meetup_notes: e.target.value })} placeholder="Ej. Mall del Sol, Guayaquil" data-testid="ship-meetup-notes" /></label>
      )}
      {f.mode === "detailed" && <div className="text-[11px] text-neutral-500">El modo detallado se edita tarifa por provincia; se conservan las tarifas actuales del vendedor.</div>}
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ship-save">{busy ? "Guardando…" : "Guardar envío"}</Button>
        <span className="text-[11px] text-neutral-500">Queda en auditoría. El vendedor puede seguir editándolo desde su panel.</span>
      </div>
    </div>
  );
}

export function SellerShippingPanel() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [provinces, setProvinces] = useState([]);
  useEffect(() => { api.get("/geo/provinces").then((r) => setProvinces(r.data || [])).catch(() => {}); }, []);
  const search = async (e) => {
    e?.preventDefault();
    try { setRows((await api.get("/admin/seller-shipping/search", { params: { q } })).data); setOpen(null); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const onSaved = (u) => setRows((rs) => rs.map((r) => (r.id === u.id ? { ...r, ...u } : r)));
  return (
    <div data-testid="seller-shipping-panel">
      <h2 className="text-xl font-semibold mb-1">Envíos de vendedores</h2>
      <p className="text-xs text-neutral-500 mb-4">Busca un vendedor por email, nombre o un pedido suyo para ver y editar su configuración de envío.</p>
      <form onSubmit={search} className="flex gap-2 mb-4 max-w-xl">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="email, nombre o nº de pedido (ej. 5e6c6065)" data-testid="ship-search-input" />
        <Button type="submit" data-testid="ship-search-btn">Buscar</Button>
      </form>
      {rows && !rows.length && <div className="text-sm text-neutral-500" data-testid="ship-no-results">Sin resultados.</div>}
      <div className="space-y-3">
        {(rows || []).map((s) => (
          <div key={s.id} className="p-3 rounded border border-neutral-800 bg-neutral-950" data-testid={`ship-seller-${s.id}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <b className="text-sm">{s.name || "—"}</b><span className="text-xs text-neutral-500">{s.email}</span>
              {s.from_order && <span className="text-[10px] px-2 py-0.5 rounded bg-sky-500/15 text-sky-300">vendedor del pedido</span>}
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => setOpen(open === s.id ? null : s.id)} data-testid={`ship-edit-${s.id}`}>{open === s.id ? "Cerrar" : "Editar envío"}</Button>
            </div>
            <Summary s={s} />
            {open === s.id && <Editor key={JSON.stringify(s.shipping_config)} s={s} provinces={provinces} onSaved={onSaved} />}
          </div>
        ))}
      </div>
    </div>
  );
}
